import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import { useNotifications } from 'src/notifications';
import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';
import { getApiErrorMessage } from 'src/services/api/utils';
import { GET_PROPERTIES_PATH } from 'src/services/api/properties/constants';
import type { IProperty } from 'src/services/api/properties/types';

type IUploadPropertyImageRequest = {
    propertyId: number;
    image: Blob;
};

type IUploadPropertyImageResponse = {
    property: IProperty;
};

export const useUploadPropertyImage = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        IProperty,
        IStandardErrorResponse,
        IUploadPropertyImageRequest
    >(
        async ({ propertyId, image }) => {
            const data = new FormData();
            data.append('image', image);

            const res = await apiClient(`${GET_PROPERTIES_PATH}/${propertyId}/image`, {
                method: 'post',
                body: data,
            }).json<IUploadPropertyImageResponse>();

            return res.property;
        }, {
            onSuccess: (data) => {

                // only image_url changes, so patch it into every cached copy of this
                // property without clobbering option-variant fields like `facts`/`parent`
                queryClient.setQueryData<IProperty[]>([queryKeys.properties], (oldData) =>
                    (oldData ?? []).map((property) =>
                        property.id === data.id ? { ...property, image_url: data.image_url } : property,
                    ),
                );

                queryClient.setQueriesData<IProperty | undefined>(
                    [queryKeys.properties, data.id],
                    (oldData) => (oldData ? { ...oldData, image_url: data.image_url } : oldData),
                );
            }, onError: (error) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to update property image';
                addNotification(errorMessage, 'error');
            }
        });
};
